import { Stack, TextField } from '@mui/material';
import { deleteWitness, ICaseDetailsInputs, witnessesEmptyObject } from 'API';
import { AddPaperCard, FormSection, RadioButtons } from 'Components';
import { yesNoOptions } from 'Constants';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary } from 'Hooks';
import { useValidationErrorContext } from 'Modules/CaseDetails';
import { IndexContext, IndexProvider } from 'Modules/CaseDetails/CaseContext';
import { DeleteModal } from 'Modules/CaseDetails/SixthSection/DeleteModal';
import { WitnessCard } from 'Modules/CaseDetails/WitnessCard';
import { useContext, useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { validateLettersNumbersAndSpacialChars } from 'Utils/validateLettersNumbersAndSpacialChars';

export const KnownWitnesses = () => {
  const { index, fieldId } = useContext(IndexContext);
  const {
    register,
    getValues,
    formState: { errors },
    handleSubmit,
  } = useFormContext<ICaseDetailsInputs>();

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const witnessIndex = useRef<number>(0);

  const [openWitnessCards, setOpenWitnessCards] = useState<number[]>([0]);

  const { registerValidationErrorHandler, unregisterValidationErrorHandler } =
    useValidationErrorContext();

  const dictionary = useDictionary(Dictionary, DICTIONARY.CASE_DETAILS);

  const baseName = `caseDetails.${index}`;

  const { remove, append, fields } = useFieldArray({
    name: `${baseName}.witnesses` as 'caseDetails.0.witnesses',
  });

  const [areThereOtherWitnesses] = useWatch({
    name: [`${baseName}.areThereOtherWitnesses`],
  });

  const { caseDetails = [] } = getValues();
  const caseIndex = Number.parseInt(baseName.split('.')[1]);

  useEffect(() => {
    if (fields.length === 0) {
      onAddWitnessHandler();
    }
  }, []);

  const openAllWitnessCards = () => {
    const allIndexes = fields.map((_, index) => index);
    setOpenWitnessCards(allIndexes);
  };

  useEffect(() => {
    registerValidationErrorHandler(index, openAllWitnessCards);
    return () => {
      unregisterValidationErrorHandler(index);
    };
  }, [index, fields.length]);

  const toggleWitnessCard = (cardIndex: number) => {
    setOpenWitnessCards((prev) =>
      prev.includes(cardIndex)
        ? prev.filter((i) => i !== cardIndex)
        : [...prev, cardIndex]
    );
  };

  const onDeleteHandler = (cardIndex: number) => {
    setShowDeleteModal(true);
    witnessIndex.current = cardIndex;
  };

  const onAddWitnessHandler = async () => {
    if (fieldId) {
      append({ ...witnessesEmptyObject });
    }
  };

  const deleteModal = async (cardIndex: number) => {
    const witness = getWitness();
    if (witness?.id) await deleteWitness(witness.id);
    remove(cardIndex);
    setShowDeleteModal(false);

    setOpenWitnessCards((prev) =>
      prev
        .filter((i) => i !== cardIndex)
        .map((i) => (i > cardIndex ? i - 1 : i))
    );
  };

  const getWitness = () => {
    if (caseDetails && caseDetails[caseIndex]) {
      if (caseDetails[caseIndex].witnesses) {
        if (caseDetails[caseIndex].witnesses![witnessIndex.current]) {
          return caseDetails[caseIndex].witnesses![witnessIndex.current];
        }
      }
    }
  };

  const witnessDetailsSectionServiceError = errors.caseDetails
    ? errors.caseDetails[index]
    : undefined;

  const witness = getWitness();
  const fullName = `${witness?.firstName} ${witness?.lastName}`;

  return (
    <>
      <FormSection disablePadding>
        {fields.map((field, cardIndex) => (
          <IndexProvider
            key={cardIndex}
            index={cardIndex}
            baseName={`${baseName}.witnesses`}
          >
            <WitnessCard
              key={field.id}
              index={cardIndex}
              caseId={fieldId}
              deleteWitness={() => onDeleteHandler(cardIndex)}
              isOpen={openWitnessCards.includes(cardIndex)}
              toggleCard={() => toggleWitnessCard(cardIndex)}
            />
          </IndexProvider>
        ))}
        <AddPaperCard
          label={dictionary.sixthSectionAddNewWitness}
          add={() => {
            handleSubmit(
              (data) => {
                onAddWitnessHandler();
                const newIndex = fields.length;
                setOpenWitnessCards((prev) => [...prev, newIndex]);
              },
              (validationErrors) => {
                openAllWitnessCards();

                setTimeout(() => {
                  const firstErrorElement =
                    document.querySelector('[role="alert"]');
                  if (firstErrorElement) {
                    firstErrorElement.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                    });
                    const inputElement =
                      firstErrorElement.querySelector('input');
                    if (inputElement) {
                      inputElement.focus();
                    }
                  }
                }, 500);
              }
            )();
          }}
        />
      </FormSection>
      {showDeleteModal && (
        <DeleteModal
          isVisible={showDeleteModal}
          closeModal={() => setShowDeleteModal(false)}
          deleteModal={() => deleteModal(witnessIndex.current)}
          texts={{ name: fullName || '' }}
        />
      )}
      <Stack gap='20px'>
        <RadioButtons
          id={dictionary.fourthSectionWitnessMore}
          name={`${baseName}.areThereOtherWitnesses` as const}
          options={yesNoOptions}
        />
        {areThereOtherWitnesses && (
          <TextField
            id={`${dictionary.fourthSectionWitnessDetails}-${index}`}
            label={dictionary.fourthSectionWitnessDetails}
            sx={{ mt: '20px' }}
            inputProps={{
              maxLength: '500',
            }}
            {...register(
              `${baseName}.otherWitnessesDetails` as `caseDetails.0.otherWitnessesDetails`
            )}
            multiline
            rows={6}
            onChange={(event: any) =>
              validateLettersNumbersAndSpacialChars(event)
            }
            role={
              Boolean(witnessDetailsSectionServiceError?.otherWitnessesDetails)
                ? 'alert'
                : undefined
            }
            error={Boolean(
              witnessDetailsSectionServiceError?.otherWitnessesDetails
            )}
          />
        )}
      </Stack>
    </>
  );
};
